import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { vehicleApi } from '../services/api';
import { 
  ArrowLeftIcon,
  SparklesIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { isTokenValid, clearAdminAuth } from '../utils/auth';

const AdminSocialMedia: React.FC = () => {
  const navigate = useNavigate();
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [selectedVehicleDetails, setSelectedVehicleDetails] = useState<any>(null);
  const [generatedPost, setGeneratedPost] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [customDriveType, setCustomDriveType] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token || !isTokenValid(token)) {
      clearAdminAuth();
      navigate('/admin/login', { replace: true });
    }
  }, [navigate]);

  // Fetch vehicles for selection
  const { data: vehicles, isLoading } = useQuery(
    ['vehicles-for-social', searchKeyword],
    async () => {
      const response = await vehicleApi.searchVehicles({ 
        page: 1,
        page_size: 50,
        keyword: searchKeyword || undefined
      });
      return response.items;
    }
  );

  const generateSocialPost = async () => {
    if (!selectedVehicle) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (!token || !isTokenValid(token)) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
        return;
      }
      const response = await fetch('/api/v1/admin/generate-social-post', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          vehicle_id: selectedVehicle.id
        })
      });

      if (!response.ok) throw new Error('Failed to generate post');

      const data = await response.json();
      setGeneratedPost(data.post);
    } catch (err) {
      console.error('Error generating post:', err);
      // Fallback to template if API fails
      generateTemplatePost();
    } finally {
      setLoading(false);
    }
  };

  // Auto-detect drive type from model name
  const detectDriveType = (model: string): string | null => {
    if (!model) return null;
    const modelUpper = model.toUpperCase();
    
    // AWD patterns
    if (modelUpper.includes('4MATIC') || 
        modelUpper.includes('XDRIVE') || 
        modelUpper.includes('QUATTRO') ||
        modelUpper.includes('4WD') ||
        modelUpper.includes('AWD')) {
      return 'AWD (4x4)';
    }
    
    // RWD patterns (common in BMWs without xDrive, Mercedes without 4MATIC)
    if (modelUpper.includes('RWD')) {
      return 'RWD (Задно)';
    }
    
    // FWD patterns
    if (modelUpper.includes('FWD')) {
      return 'FWD (Предно)';
    }
    
    return null;
  };

  const generateTemplatePost = () => {
    if (!selectedVehicleDetails) return;

    const v = selectedVehicleDetails;
    
    // Detect or use custom drive type
    const autoDriveType = detectDriveType(v.model);
    const driveType = customDriveType || autoDriveType;
    
    // Build technical details array
    const techDetails = [];
    if (v.engine) techDetails.push(`🔧 Двигател: ${v.engine}`);
    if (v.transmission) techDetails.push(`⚙️ Скоростна кутия: ${v.transmission}`);
    if (v.odometer) techDetails.push(`📊 Пробег: ${v.odometer.toLocaleString()} ${v.odometer_unit || 'km'}`);
    if (v.fuel_type) techDetails.push(`⛽ Гориво: ${v.fuel_type}`);
    if (driveType) techDetails.push(`🔄 Задвижване: ${driveType}`);
    
    // Use custom price if provided, otherwise calculate estimate
    const priceText = customPrice 
      ? `💰 Очаквана цена до България: ${customPrice} €`
      : '💰 Очаквана цена до България: [ЦЕНА] €';
    
    // Determine location and hashtags based on country
    const location = v.country === 'USA' ? 'САЩ' : 'Канада';
    const locationHashtag = v.country === 'USA' ? '#САЩ' : '#канада';
    
    // Create template post in Bulgarian
    const post = `🚗 ${v.make} ${v.model} ${v.year}

📋 Технически данни:
${techDetails.join('\n')}

${v.primary_damage ? `⚠️ Щети: ${v.primary_damage}` : '✅ Състояние: Отлично'}
📍 Локация: ${location}

${priceText}

${v.country === 'CANADA' ? '✅ Наличен Carfax!' : ''}
🚚 Доставка: 2 до 3 месеца

${v.auction_data?.auction_date ? `⏰ Търг: ${new Date(v.auction_data.auction_date).toLocaleDateString('bg-BG')}` : ''}

📞 За повече информация - свържете се с нас!
🌐 Вижте повече на нашия сайт - carcube.bg

#${v.make?.replace(/\s+/g, '')} #${v.model?.replace(/\s+/g, '')} #внос #автомобил #импорт ${locationHashtag} #CarCube`;

    setGeneratedPost(post);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedPost);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = generatedPost;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error('Failed to copy:', e);
        alert('Failed to copy to clipboard. Please copy manually.');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Social Media Posts</h1>
              <p className="text-sm text-gray-600">Generate posts for Facebook, Instagram & Viber</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Vehicle Selection */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Vehicle</h2>
            
            {/* Search */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search by make, model, VIN..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Vehicle List */}
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {isLoading ? (
                <div className="text-center py-8 text-gray-500">Loading vehicles...</div>
              ) : vehicles && vehicles.length > 0 ? (
                vehicles.map((vehicle: any) => (
                  <button
                    key={vehicle.id}
                    onClick={async () => {
                      setSelectedVehicle(vehicle);
                      // Fetch full vehicle details with images
                      try {
                        const fullVehicle = await vehicleApi.getVehicle(vehicle.id);
                        setSelectedVehicleDetails(fullVehicle);
                      } catch (err) {
                        console.error('Failed to fetch vehicle details:', err);
                        setSelectedVehicleDetails(vehicle);
                      }
                    }}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedVehicle?.id === vehicle.id
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {vehicle.stock_number} • {vehicle.location}
                    </div>
                    {vehicle.current_bid && (
                      <div className="text-sm font-semibold text-primary-600 mt-1">
                        {Math.round(vehicle.current_bid).toLocaleString()} €
                      </div>
                    )}
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">No vehicles found</div>
              )}
            </div>

            {/* Price Input & Generate Buttons */}
            {selectedVehicleDetails && (
              <div className="mt-6 space-y-3">
                {/* Custom Price Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    💰 Очаквана цена до България (optional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="напр. 25000"
                      value={customPrice}
                      onChange={(e) => setCustomPrice(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <span className="flex items-center text-gray-600 font-medium">€</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty to add price manually later
                  </p>
                </div>

                {/* Custom Drive Type Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    🔄 Задвижване (optional)
                  </label>
                  <input
                    type="text"
                    placeholder={detectDriveType(selectedVehicleDetails?.model) || "напр. AWD (4x4), FWD, RWD"}
                    value={customDriveType}
                    onChange={(e) => setCustomDriveType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {detectDriveType(selectedVehicleDetails?.model) 
                      ? `Auto-detected: ${detectDriveType(selectedVehicleDetails?.model)}` 
                      : 'Not detected - enter manually if needed'}
                  </p>
                </div>

                <button
                  onClick={generateSocialPost}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Generating with AI...
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-5 h-5 mr-2" />
                      Generate AI Post (Bulgarian)
                    </>
                  )}
                </button>
                
                <button
                  onClick={generateTemplatePost}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center"
                >
                  Generate Template Post
                </button>
              </div>
            )}
          </div>

          {/* Right: Generated Post & Images */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Generated Post & Images</h2>
            
            {/* Vehicle Images */}
            {selectedVehicleDetails && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Vehicle Images ({selectedVehicleDetails.images?.length || 0})</h3>
                {selectedVehicleDetails.images && selectedVehicleDetails.images.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {selectedVehicleDetails.images.slice(0, 6).map((img: any, index: number) => (
                      <div key={index} className="relative group">
                        <img
                          src={img.image_url}
                          alt={`${selectedVehicle.make} ${selectedVehicle.model} - ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200?text=No+Image';
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-center justify-center">
                          <a
                            href={img.image_url}
                            download={`${selectedVehicle.stock_number}_${index + 1}.jpg`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="opacity-0 group-hover:opacity-100 bg-white text-gray-900 px-3 py-1 rounded-lg text-xs font-semibold transition-all"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    No images available for this vehicle
                  </div>
                )}
                <button
                  onClick={async () => {
                    if (!selectedVehicleDetails.images || selectedVehicleDetails.images.length === 0) {
                      alert('No images to download');
                      return;
                    }
                    // Download all images
                    selectedVehicleDetails.images.forEach((img: any, index: number) => {
                      const link = document.createElement('a');
                      link.href = img.image_url;
                      link.download = `${selectedVehicle.stock_number}_${index + 1}.jpg`;
                      link.target = '_blank';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    });
                  }}
                  className="w-full text-sm bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  📥 Download All Images ({selectedVehicleDetails.images?.length || 0})
                </button>
              </div>
            )}
            
            {generatedPost ? (
              <>
                {/* Editable Post */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Edit Post (you can modify before copying)
                  </label>
                  <textarea
                    value={generatedPost}
                    onChange={(e) => setGeneratedPost(e.target.value)}
                    className="w-full min-h-[400px] max-h-[600px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-sans text-sm text-gray-900 resize-y"
                    rows={20}
                  />
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  <button
                    onClick={copyToClipboard}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center"
                  >
                    {copied ? (
                      <>
                        <CheckCircleIcon className="w-5 h-5 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="w-5 h-5 mr-2" />
                        Copy to Clipboard
                      </>
                    )}
                  </button>

                  <div className="flex gap-3">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-center"
                    >
                      Share on Facebook
                    </a>
                    <button
                      onClick={() => {
                        alert('Copy the post above and paste it into Instagram or Viber manually');
                      }}
                      className="flex-1 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Instagram
                    </button>
                    <button
                      onClick={() => {
                        alert('Copy the post above and paste it into Viber manually');
                      }}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Viber
                    </button>
                  </div>
                </div>

                {/* Tips */}
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-900 font-semibold mb-2">💡 Tips:</p>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>Copy the text and paste directly into your social media post</li>
                    <li>Add vehicle photos from your database</li>
                    <li>Customize hashtags for better reach</li>
                    <li>Best posting times: 10-11 AM and 7-9 PM</li>
                  </ul>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-[400px] text-gray-400">
                <div className="text-center">
                  <SparklesIcon className="w-16 h-16 mx-auto mb-4" />
                  <p>Select a vehicle and generate a post</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSocialMedia;

